<template>
  <app-page full-width labelledby="page-title">
    <page-header title="История платежей" description="Счета на пополнение, зачисления и списания по тарифу.">
      <template #actions>
        <q-btn flat no-caps label="К тарифу" :to="{ name: 'tarif_plan' }" />
        <q-btn v-if="canChangeBilling" color="primary" no-caps icon="add" label="Пополнить баланс" @click="paymentDialog = true" />
      </template>
    </page-header>

    <app-data-table
      title="Платежи"
      :count="invoices.length"
      :rows="invoices"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="stateMessage"
      storage-key="organization-payments"
      @retry="requestPayments"
    >
      <template #body-cell-date="props"><q-td :props="props">{{ formatDate(props.value) }}</q-td></template>
      <template #body-cell-amount="props"><q-td :props="props" class="text-right text-weight-medium">{{ money(props.value) }}</q-td></template>
      <template #body-cell-status="props"><q-td :props="props"><status-badge v-bind="statusProps(props.value)" /></q-td></template>
      <template #body-cell-actions="props">
        <q-td :props="props" class="text-right">
          <q-btn flat no-caps label="Открыть" :disable="!hasDocument(props.row)" @click="openDocument(props.row)" />
        </q-td>
      </template>
    </app-data-table>
    <p v-if="!canChangeBilling && permissionsLoaded" class="readonly-note">История доступна для просмотра. Для создания счета нужны права «Тарифы и счета».</p>

    <q-dialog v-model="paymentDialog" persistent>
      <q-card class="payment-dialog">
        <q-card-section><h2>Пополнить баланс</h2><p>После создания счет появится в документах и в этой истории.</p></q-card-section>
        <q-card-section><q-input :model-value="moneyInput(paymentAmount, 'paymentAmount')" @update:model-value="setMoney('paymentAmount', $event)" @blur="finishMoney('paymentAmount')" type="number" min="1" label="Сумма, ₽" :rules="[() => Number(paymentAmount) > 0 || 'Введите сумму больше нуля']" /></q-card-section>
        <q-card-actions align="right">
          <q-btn flat no-caps label="Отмена" :disable="paymentLoading" v-close-popup />
          <q-btn color="primary" no-caps label="Создать счет" :loading="paymentLoading" :disable="paymentAmount <= 0" @click="createPayment" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </app-page>
</template>

<script>
import { formatMoney } from '../../../services/moneyFormat'
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import AppPage from '../../../components/ui/AppPage.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import StatusBadge from '../../../components/ui/StatusBadge.vue'

const COLUMNS = Object.freeze([
  { name: 'id', label: 'Платеж №', field: 'id', sortable: true, align: 'left', required: true },
  { name: 'date', label: 'Дата', field: 'date', sortable: true, align: 'left', classes: 'column-priority-secondary', headerClasses: 'column-priority-secondary' },
  { name: 'amount', label: 'Сумма', field: 'amount', sortable: true, align: 'right', classes: 'column-priority-secondary', headerClasses: 'column-priority-secondary' },
  { name: 'status', label: 'Статус', field: 'status', sortable: true, align: 'left' },
  { name: 'actions', label: 'Действия', field: 'actions', align: 'right', required: true }
])

export default {
  name: 'OrganizationInvoices',
  components: { AppDataTable, AppPage, PageHeader, StatusBadge },
  data() {
    return {
      tableState: 'loading', stateTitle: '', stateMessage: '', invoices: [], columns: COLUMNS,
      pagination: { sortBy: 'date', descending: true, page: 1, rowsPerPage: 10 },
      canChangeBilling: false, permissionsLoaded: false, paymentDialog: false, paymentAmount: 500, paymentLoading: false, editingMoney: ''
    }
  },
  created() {
    this.onPayments = (response) => {
      if (response.error) { this.tableState = 'error'; this.stateTitle = 'Не удалось загрузить историю'; this.stateMessage = 'Проверьте подключение и повторите запрос.'; return }
      this.invoices = Array.isArray(response.data) ? response.data : []
      this.tableState = this.invoices.length ? 'ready' : 'empty'
      if (!this.invoices.length) { this.stateTitle = 'Платежей пока нет'; this.stateMessage = 'Созданные счета, зачисления и списания появятся здесь.' }
    }
    this.onUser = (response) => { this.canChangeBilling = response.user?.role?.permissions?.change_billing === true; this.permissionsLoaded = true }
    this.onPayment = (response) => {
      this.paymentLoading = false
      if (response.error || !response.data?.id) { this.$q.notify({ color: 'negative', message: 'Не удалось создать счет' }); return }
      this.paymentDialog = false; this.$q.notify({ color: 'positive', message: 'Счет на оплату создан' })
      this.$router.push({ name: 'document_info_bill_to_pay', params: { id: response.data.id, history_id: 'last' } })
    }
    this.emitter.on('onGetPaymentsList', this.onPayments); this.emitter.on('didReceoveUserInfoAcrossOrg', this.onUser); this.emitter.on('onCreatePayment', this.onPayment)
    if (this.connection.isAuthenticated) this.requestData(); else this.emitter.on('connectionReady', this.requestData)
  },
  beforeUnmount() {
    this.emitter.off('onGetPaymentsList', this.onPayments); this.emitter.off('didReceoveUserInfoAcrossOrg', this.onUser); this.emitter.off('onCreatePayment', this.onPayment); this.emitter.off('connectionReady', this.requestData)
  },
  methods: {
    moneyInput(value, field) {
      if (this.editingMoney === field) return value
      return formatMoney(value, { empty: '', invalid: String(value), useGrouping: false }).replace(',', '.')
    },
    setMoney(field, value) {
      this.editingMoney = field
      const number = Number.parseFloat(value)
      this[field] = Number.isNaN(number) ? value : number
    },
    finishMoney(field) {
      if (this.editingMoney === field) this.editingMoney = ''
    },
    requestData() { this.requestPayments(); this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('payments-permission')) },
    requestPayments() { this.tableState = 'loading'; this.connection.getPaymentsList(this.connection.makeRequestId('payments')) },
    statusProps(status) {
      const value = String(status || '').toLowerCase()
      return {
        paid: { label: 'Оплачен', tone: 'success', icon: 'check_circle' }, pending: { label: 'Ожидает оплаты', tone: 'warning', icon: 'schedule' },
        send: { label: 'Списан', tone: 'info', icon: 'south_west' }, fail: { label: 'Не оплачен', tone: 'danger', icon: 'error_outline' }
      }[value] || { label: 'Неизвестен', tone: 'neutral', icon: 'help_outline' }
    },
    formatDate(value) { if (!value) return '—'; const date = new Date(value); return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('ru-RU').format(date) },
    money(value) { return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(Number(value || 0)) },
    hasDocument(invoice) { return !!(invoice.metadata?.document_id || invoice.metadata?.upd_id) },
    openDocument(invoice) {
      const metadata = invoice.metadata || {}
      if (metadata.upd_id) this.$router.push({ name: 'document_info_upd', params: { id: metadata.upd_id, history_id: 'last' } })
      else if (metadata.document_id) this.$router.push({ name: 'document_info_bill_to_pay', params: { id: metadata.document_id, history_id: 'last' } })
    },
    createPayment() { if (Number(this.paymentAmount) <= 0) return; this.paymentLoading = true; this.connection.createPayment(this.connection.makeRequestId('payment-create'), Number(this.paymentAmount), '', 'income') }
  }
}
</script>

<style scoped>
.readonly-note { color: var(--sid-color-text-secondary); }
.payment-dialog { width: min(28rem, calc(100vw - 2 * var(--sid-space-4))); }
.payment-dialog h2, .payment-dialog p { margin: 0; }
.payment-dialog p { margin-top: var(--sid-space-2); color: var(--sid-color-text-secondary); }
</style>
